import { AsyncPipe, DecimalPipe, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { dateFormatPipe } from '@app/pipes/datepipe';
import { ReimbursementRequestService } from '@app/v2/services/reimbursement-request/reimbursement-request.service';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { Chip, MdsChipComponent } from '@medpacesoftwaredevelopment/designsystem';
import { Expense } from '@models/request';
import { CountryStateService } from '@services/state-management/country-state.service';
import { PatientStateService } from '@services/state-management/patient-state.service';
import { parseDistanceUnit } from '@utility/utility';
import { combineLatest, filter, map } from 'rxjs';
import { computeAuthorizedAmountPatientsCurrencyChip, computeAuthorizedAmountUSDChip } from '../expense-chips.util';

@Component({
    selector: 'medpace-mileage-expense-view',
    imports: [AsyncPipe, dateFormatPipe, UpperCasePipe, DecimalPipe, MdsChipComponent],
    templateUrl: './mileage-expense-view.component.html',
    styleUrl: './mileage-expense-view.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MileageExpenseViewComponent {
    expense = input.required<Expense>();
    expense$ = toObservable(this.expense).pipe(filter(Boolean));
    elevatedAccess$ = inject(UserServiceV2).permissions.request.access.elevated$;
    isInProcessOrHigher$ = inject(ReimbursementRequestService).isInProcessOrHigher$;
    patient = toSignal(inject(PatientStateService).getPatient());

    private readonly patientPreferredCurrency = computed(
        () => this.patient()?.preferredCurrency?.toUpperCase() ?? null
    );

    authorizedAmountPatientsCurrencyChip = computed((): Chip[] | null =>
        computeAuthorizedAmountPatientsCurrencyChip(this.expense(), this.patientPreferredCurrency())
    );

    authorizedAmountUSDChip = computed((): Chip[] | null => computeAuthorizedAmountUSDChip(this.expense()));
    country$ = combineLatest({
        countries: inject(CountryStateService).getActiveCountries(),
        expense: this.expense$,
    }).pipe(
        map(({ countries, expense }) => {
            const expenseCountryLowerCase = expense.mileageReimbursementCountry.toLowerCase();
            const country = countries.find(
                (country) =>
                    country.threeCharCode.toLowerCase() === expenseCountryLowerCase ||
                    country.name.toLowerCase() === expenseCountryLowerCase
            );
            return country;
        })
    );
    distanceUnit = computed(() => {
        const distanceUnit = parseDistanceUnit(this.expense().mileageDistanceUnits);
        return distanceUnit ?? undefined;
    });
}
